import FormInput from 'components/ui/composed/declarations/formInput/DeclarationInput';
import DeclarationNumberInput from 'components/ui/composed/declarations/formInput/DeclarationNumberInput';
import FormSelect from 'components/ui/composed/declarations/formSelect/DeclarationSelect';
import { useTemplateContext } from 'components/ui/composed/template/TemplateContext';
import { FormikProvider } from 'formik';
import useTooltips from 'hooks/useTooltips';
import { get, set } from 'lodash';
import { FC, FocusEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getBox44FieldPath, handleBox44Field } from 'views/declarations/common/box44/box-44-utils';
import { DeclarationFormCardProps } from 'views/declarations/common/declaration.form.card';
import { getFormikProps, handleToggleHelp } from 'views/declarations/utils/form-utils';
import NewFormCard, { FormCardContainer } from '../../../../common/cards/NewFormCard';
import AutoFillModal from '../components/AutoFillModal';
import useNumberOfItemsModal from 'views/declarations/declaration-tabs/declaration-form/utils/useNumberOfItemsModal';
import useProducts from 'hooks/useProducts';
import useCodelists from '../../../../../../hooks/useCodelists';
import { validators } from 'views/declarations/uk/export/validations/validations';
import { useOutletContext } from 'react-router-dom';
import { Events } from 'utils/events.enum';
import { h1Box44KeyNames, h1PathBox44 } from 'views/declarations/common/box44/mappers';

const PREV_DOC_PATH = 'goodsShipment.simplifiedDeclarationDocumentWritingOff';

const MasterDetailsCard: FC<DeclarationFormCardProps> = (props) => {
    const { t } = useTranslation('form');
    const { aisCodelists } = useCodelists();
    const { getH1TooltipsByRefNumber } = useTooltips();
    const { template, form, templateFormik } = useTemplateContext();
    const { createIrelandImportProduct, listIrelandH1Products } = useProducts();

    const [open, setOpen] = useState(false);
    const [value, setValue] = useState<string | null>(null);
    const [prevValue, setPrevValue] = useState<string | null>(null);
    // Ref avoids the blur race: DeclarationInput calls onChange then onBlur in the same event.
    // setState in onChange is not visible to onBlur's closed-over state value.
    const invoiceNumberChangedRef = useRef(false);
    // Remember last non-empty invoice so remove-autofill can match related fields
    // even when prevValue was never set (or was cleared by closing a remove modal).
    const lastNonEmptyInvoiceRef = useRef<string | null>(null);

    const { saveAsDraft } =
        useOutletContext<{
            saveAsDraft: (withNotification: boolean, data?: unknown) => Promise<unknown>;
        }>() ?? {};

    const setInvoiceNumberChanged = useCallback((changed: boolean) => {
        invoiceNumberChangedRef.current = changed;
    }, []);

    const openModal = useCallback(
        (nextValue: string | null) => {
            setInvoiceNumberChanged(false);
            if (!nextValue && lastNonEmptyInvoiceRef.current) {
                setPrevValue(lastNonEmptyInvoiceRef.current);
            }
            setOpen(true);
            setValue(nextValue);
        },
        [setInvoiceNumberChanged]
    );
    const closeModal = () => {
        setOpen(false);
        if (value) {
            setPrevValue(value);
            lastNonEmptyInvoiceRef.current = value;
        }
        setValue(null);
    };

    const formik = useMemo(() => {
        if (template && templateFormik) return templateFormik;
        return props.formik;
    }, [props.formik, template, templateFormik]);

    const prevDocumentsIndex = useMemo(() => {
        const prevDocsRecords = formik?.getFieldProps(PREV_DOC_PATH).value;

        if (!prevDocsRecords) return 0;

        const prevDocWithType380Index = prevDocsRecords?.findIndex(
            (prevDoc: any) => prevDoc?.previousDocumentType === '380'
        );

        if (prevDocWithType380Index !== -1) return prevDocWithType380Index;

        return prevDocsRecords.length;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik?.getFieldProps(PREV_DOC_PATH).value]);

    const autoFill = (fields: string[]) => {
        const values = formik?.values;
        const newValues = fields.reduce((acc, field) => {
            let _value = value;
            let _field = field;

            if (field.includes(PREV_DOC_PATH)) {
                const path = template ? `master.defaults.${PREV_DOC_PATH}` : PREV_DOC_PATH;
                let previousDocuments: any[] | undefined = get(values, path);
                previousDocuments = previousDocuments ? [...previousDocuments] : [];
                if (!value) {
                    previousDocuments.splice(prevDocumentsIndex, 1);
                } else {
                    previousDocuments[prevDocumentsIndex] = {
                        previousDocumentType: '380',
                        previousDocumentIdentifier: value,
                    };
                }
                _value = previousDocuments as any;
                _field = path;
            } else if (field.includes(h1PathBox44)) {
                const path = template ? `master.defaults.${h1PathBox44}` : h1PathBox44;
                let box44 = { ...((get(values, path) as Record<string, any> | undefined) ?? {}) };
                if (!value) {
                    delete box44.N730;
                } else {
                    box44.N730 = {
                        [h1Box44KeyNames.documentType]: 'N730',
                        [h1Box44KeyNames.documentIdentifier]: value,
                    };
                }
                _value = box44 as any;
                _field = path;
            }

            return set(acc, _field, _value);
        }, values);
        formik?.setValues(newValues);
    };

    const [handleNumberOfItems, modalContextHolder] = useNumberOfItemsModal({
        createProduct: createIrelandImportProduct,
        listProducts: listIrelandH1Products,
        saveAsDraft,
    });

    const invoiceNumberFormikProps = useMemo(
        () => getFormikProps(getBox44FieldPath({ path: h1PathBox44, keyNames: h1Box44KeyNames, type: 'N935' }), props),
        [props]
    );

    const handleInvoiceNumberBlur = useCallback(
        (e?: FocusEvent<HTMLInputElement>) => {
            const value = e?.target?.value ?? invoiceNumberFormikProps.fieldMeta?.value;
            /**
             * NOTE
             * Cheating the error validation by doing another validation
             * that is not connected to the actual validations.
             * Going in the "right" route will required more code and
             * it will become unnecessarily complicated.
             */
            if (!invoiceNumberChangedRef.current) return;
            const invoiceNumberError = value.length > 0 && value.length < 5;
            if (invoiceNumberError) return;
            openModal(value);
        },
        [invoiceNumberFormikProps.fieldMeta?.value, openModal]
    );

    useEffect(() => {
        const changeSubscriberId = window.EventBus.subscribe(Events.INVOICE_NUMBER_CHANGED, setInvoiceNumberChanged);
        const invoiceModalSavedSubscriberId = window.EventBus.subscribe(Events.INVOICE_MODAL_SAVED, () =>
            handleInvoiceNumberBlur()
        );
        return () => {
            window.EventBus.unsubscribe(changeSubscriberId);
            window.EventBus.unsubscribe(invoiceModalSavedSubscriberId);
        };
    }, [handleInvoiceNumberBlur, setInvoiceNumberChanged]);

    return (
        <FormikProvider value={props.formik!}>
            <NewFormCard title={t('masterDetails')}>
                <FormCardContainer>
                    <FormSelect
                        required
                        viewOnly={props.viewOnly}
                        {...getFormikProps(`declarationType`, props)}
                        refNumber="1.1"
                        label={t('declarationType')}
                        tooltip={getH1TooltipsByRefNumber('1.1')}
                        refClicked={(ref) => handleToggleHelp(ref, props)}
                        selectOptions={aisCodelists?.typeOfDeclaration}
                        disabled={props.amendment}
                        condensed
                        codeValidation={[validators.exact(2)]}
                    />

                    <FormSelect
                        required
                        disabled={props.amendment}
                        viewOnly={props.viewOnly}
                        {...getFormikProps(`additionalDeclarationType`, props)}
                        refNumber="1.2"
                        label={t('additionalDeclarationType')}
                        tooltip={getH1TooltipsByRefNumber('1.2')}
                        refClicked={(ref) => handleToggleHelp(ref, props)}
                        selectOptions={aisCodelists?.additionalDeclarationType}
                        condensed
                        codeValidation={[validators.exact(1)]}
                    />

                    <FormInput
                        maxLength={35}
                        viewOnly={props.viewOnly}
                        {...getFormikProps(`goodsShipment.ucr`, props)}
                        refNumber="2.4"
                        label={t('ucr')}
                        tooltip={getH1TooltipsByRefNumber('2.4')}
                        refClicked={(ref) => handleToggleHelp(ref, props)}
                        condensed
                    />

                    <DeclarationNumberInput
                        viewOnly={props.viewOnly}
                        {...getFormikProps(`numberOfItems`, props)}
                        fieldEvents={{
                            onBlur(value) {
                                handleNumberOfItems(Number(value));
                            },
                        }}
                        required
                        label={t('numberOfItems')}
                        refClicked={(ref) => handleToggleHelp(ref, props)}
                        condensed
                    />
                    {modalContextHolder}

                    <FormInput
                        maxLength={512}
                        viewOnly={props.viewOnly}
                        {...invoiceNumberFormikProps}
                        onChange={(e) => {
                            const nextValue = e.target.value;
                            const { status } = handleBox44Field(
                                nextValue,
                                template && templateFormik ? templateFormik : props,
                                {
                                    path: h1PathBox44,
                                    keyNames: h1Box44KeyNames,
                                    type: 'N935',
                                },
                                { template, form }
                            );
                            if (status !== 'unchanged') {
                                setInvoiceNumberChanged(true);
                                if (nextValue) {
                                    lastNonEmptyInvoiceRef.current = nextValue;
                                }
                            }
                        }}
                        label={t('Invoice Number')}
                        tooltip={
                            getH1TooltipsByRefNumber('N935') !== 'N/A' ? getH1TooltipsByRefNumber('N935') : undefined
                        }
                        refNumber="N935"
                        refClicked={(ref) => handleToggleHelp(ref, props)}
                        onBlur={handleInvoiceNumberBlur}
                        condensed
                    />
                    <AutoFillModal
                        visible={open}
                        onCancel={closeModal}
                        fields={[
                            {
                                label: `${t('roadConsignmentNote')} (Box 44: N730)`,
                                name: `${h1PathBox44}.N730.${h1Box44KeyNames.documentIdentifier}`,
                            },
                            {
                                label: `Commercial invoice (Previous document: 380)`,
                                name: `${PREV_DOC_PATH}.${prevDocumentsIndex}.previousDocumentIdentifier`,
                            },
                        ]}
                        value={value}
                        prevValue={prevValue}
                        onOk={autoFill}
                    />

                    <DeclarationNumberInput
                        viewOnly={props.viewOnly}
                        {...getFormikProps(`grossMass`, props)}
                        refNumber="6.5"
                        label={t('goodsItems.grossMass')}
                        refClicked={(ref) => handleToggleHelp(ref, props)}
                        condensed
                    />
                </FormCardContainer>
            </NewFormCard>
        </FormikProvider>
    );
};

export default MasterDetailsCard;
